import { Navigate, Route, Routes, Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth/useAuth.js';
import { api } from './api/client.js';
import { LoginPage } from './pages/LoginPage.js';
import { BoardPage } from './pages/BoardPage.js';
import { CasePage } from './pages/CasePage.js';
import { useEventStream } from './realtime/useEventStream.js';
import { IncomingCall } from './components/IncomingCall.js';

/**
 * Connection state, shown only when it is bad.
 *
 * A green "live" badge is noise a dispatcher learns to ignore within a shift.
 * Silence means working; a visible warning means the board may be stale, which
 * is the only state they need to act on.
 */
function StreamIndicator({ state }: { state: ReturnType<typeof useEventStream>['state'] }) {
  if (state === 'live') return null;
  return (
    <span
      className={
        'rounded px-2 py-0.5 text-[11px] font-medium ' +
        (state === 'offline' ? 'bg-amber-600/20 text-amber-300' : 'bg-slate-700 text-slate-300')
      }
      role={state === 'offline' ? 'alert' : undefined}
    >
      {state === 'offline' ? 'Reconnecting — this board may be out of date' : 'Connecting…'}
    </span>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  // One connection for the whole app, mounted here so it lives across
  // navigation rather than reconnecting on every route change.
  const { state, ringing, dismissCall } = useEventStream();

  return (
    <div className="min-h-screen">
      <header className="flex items-center gap-4 border-b border-line bg-surface-2 px-5 py-2.5">
        <Link to="/" className="text-sm font-semibold text-slate-100">
          Medi Response One
        </Link>
        <span className="ml-auto flex items-center gap-3">
          <StreamIndicator state={state} />
          <span className="text-xs text-slate-400">{user?.name}</span>
        </span>
        <button
          onClick={() => {
            // void: React does not await an onClick handler, so returning a
            // promise here silently discards rejections. Explicit is better.
            void (async () => {
              await api.post('/auth/logout');
              await qc.invalidateQueries();
            })();
          }}
          className="text-xs text-slate-400 hover:text-slate-300"
        >
          Sign out
        </button>
      </header>
      {children}

      {/* Screen-pop. Rendered at the shell so a ringing call reaches a
          dispatcher wherever they are in the app, not only on the board. */}
      {ringing.map((call) => (
        <IncomingCall key={call.callId} call={call} onDismiss={() => dismissCall(call.callId)} />
      ))}
    </div>
  );
}

export function App() {
  const { user, isLoading } = useAuth();

  // Deliberately blank rather than flashing the login form. Redirecting an
  // already-signed-in user to /login for half a second is the kind of thing
  // that makes an app feel broken.
  if (isLoading) return <div className="min-h-screen" />;
  if (!user) return <LoginPage />;

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<BoardPage />} />
        <Route path="/cases/:id" element={<CasePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
